import React, { createContext, useContext, useState, useEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// Define available languages
export const LANGUAGES = {
  en: {
    code: 'en',
    name: 'English',
    flag: '🇺🇸',
  },
  zh: {
    code: 'zh',
    name: '中文',
    flag: '🇨🇳',
  },
  th: {
    code: 'th',
    name: 'ภาษาไทย',
    flag: '🇹🇭',
  },
  tl: {
    code: 'tl',
    name: 'Filipino',
    flag: '🇵🇭',
  },
};

// Create context
const KioskContext = createContext();

// Create base theme
const baseTheme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

// Format price in PHP currency
export const formatPrice = (price) => {
  // Convert to integer value for PHP display
  // Multiply by 100 to display as PHP 50 instead of PHP 0.50
  return `PHP ${(price * 100).toFixed(2)}`;
};

// Context provider component
export const KioskProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('kiosk_language') || 'en';
  });
  
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('kiosk_darkMode') === 'true' || false;
  });

  const [paymentMethod, setPaymentMethod] = useState('cash');
  
  // Order type state (dine-in or take-out)
  const [orderType, setOrderType] = useState('dine-in');
  
  // Table number state
  const [tableNumber, setTableNumber] = useState('');
  
  // Cart items state
  const [cartItems, setCartItems] = useState([]);

  // Delivery State
  const [selectedDelivery, setSelectedDelivery] = useState('');
  
  // Discount details state
  const [discountDetails, setDiscountDetails] = useState({
    discount: 0,
    discountType: 'None',
    voucherCode: '',
    customerId: null,
    orderId: null,
    extraCustomers: [],
  });

  const [discountDetails2, setDiscountDetails2] = useState([]);

  const [voucherProducts, setVoucherProducts] = useState([]);
  const clearVoucherProducts = () => setVoucherProducts([]);

  // Save preferences to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('kiosk_language', language);
  }, [language]);

  useEffect(() => {
    localStorage.setItem('kiosk_darkMode', darkMode.toString());
  }, [darkMode]);

  // Function to toggle dark mode
  const toggleDarkMode = () => {
    setDarkMode(prev => !prev);
  };

  // Function to change language
  const changeLanguage = (newLanguage) => {
    if (LANGUAGES[newLanguage]) {
      setLanguage(newLanguage);
    }
  };

  // Function to change payment method
  const changePaymentMethod = (method) => {
    setPaymentMethod(method);
  };

  // Translation function
  const translate = (text, translations) => {
    if (!translations) return text;
    return translations[language] || translations.en || text;
  };

  // Create theme based on dark mode preference
  const theme = createTheme({
    ...baseTheme,
    palette: {
      ...baseTheme.palette,
      mode: darkMode ? 'dark' : 'light',
      background: {
        default: darkMode ? '#121212' : '#f5f5f5',
        paper: darkMode ? '#1e1e1e' : '#ffffff',
      },
    },
  });

  // Function to change order type
  const changeOrderType = (type) => {
    setOrderType(type);
  };
  
  // Function to set table number
  const updateTableNumber = (number) => {
    setTableNumber(number);
  };
  
  // Function to add item to cart
  const addToCart = (item) => {
    setCartItems(prevItems => [...prevItems, item]);
  };
  
  // Function to update cart item
  const updateCartItem = (itemId, updatedItem) => {
    setCartItems(prevItems => 
      prevItems.map(item => item.id === itemId ? updatedItem : item)
    );
  };
  
  // Function to remove item from cart
  const removeFromCart = (itemId) => {
    setCartItems(prevItems => prevItems.filter(item => item.id !== itemId));
  };
  
  // Function to clear the cart
  const clearCart = () => {
    setCartItems([]);
  };
  
  // Function to update discount details
  const updateDiscountDetails = (details) => {
    setDiscountDetails(prev => {
      const next = { ...prev, ...details };
  
      if (next.discountType !== 'Voucher') {
        next.voucherCode   = '';
        next.discountDesc  = '';
        next.discount      = next.discountType === 'None' ? 0 : next.discount;
        // clearVoucherProducts();          
      }
  
      return next;
    });
  };

  const pushExtraDiscount = (payload) => {
    setDiscountDetails2(prev => {
      if (!payload) return prev;
      if (Array.isArray(payload)) return payload; 
      return [...prev, payload];                   
    });
  };
  
  // Function to reset discount
  const resetDiscount = () => {
    setDiscountDetails({
      discount: 0,
      discountType: 'None',
      voucherCode: '',
      customerId: null,
      orderId: null,
      extraCustomers: []
    });
    setDiscountDetails2([]);   
    clearVoucherProducts(); 
  };

  // Context value
  const contextValue = {
    language,
    changeLanguage,
    darkMode,
    toggleDarkMode,
    paymentMethod,
    changePaymentMethod,
    orderType,
    changeOrderType,
    tableNumber,
    updateTableNumber,
    cartItems,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    discountDetails,
    updateDiscountDetails,
    resetDiscount,
    translate,
    formatPrice,
    availableLanguages: LANGUAGES,
    selectedDelivery,
    setSelectedDelivery,
    discountDetails2,
    pushExtraDiscount,
    voucherProducts,
    setVoucherProducts,
    clearVoucherProducts
  };

  return (
    <KioskContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </KioskContext.Provider>
  );
};

// Custom hook to use the context
export const useKioskContext = () => {
  const context = useContext(KioskContext);
  if (!context) {
    throw new Error('useKioskContext must be used within a KioskProvider');
  }
  return context;
};